import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Share2, Copy, Download, RefreshCw, Loader2, MessageCircle, Facebook, Twitter, Send, Linkedin, Mail, Instagram, Image as ImgIcon } from "lucide-react";
import { toast } from "sonner";

// Colored one-tap share buttons for a published news article. Uses the
// backend /admin/news/{slug}/share-kit endpoint which serves ready-to-use
// share URLs + the pre-cached E-paper JPG. Instagram/YouTube don't support
// URL share, so we offer "Download JPG + Open App" fallback for those.
export default function SocialSharePanel({ slug, title, className = "" }) {
  const [open, setOpen] = useState(false);
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState(false);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/news/${encodeURIComponent(slug)}/share-kit`);
      setKit(data);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open && !kit) load(); }, [open]); // eslint-disable-line

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("📋 Copy हो गया");
  };

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post(`/admin/news/${encodeURIComponent(slug)}/regenerate-epaper`);
      toast.success("E-paper JPG ताज़ा बना दिया");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setRegen(false); }
  };

  const platforms = kit ? [
    { key: "whatsapp",  label: "WhatsApp",  color: "bg-emerald-500 hover:bg-emerald-600", icon: MessageCircle, url: kit.share_urls.whatsapp },
    { key: "facebook",  label: "Facebook",  color: "bg-blue-600 hover:bg-blue-700",       icon: Facebook,      url: kit.share_urls.facebook },
    { key: "twitter",   label: "X / Twitter", color: "bg-slate-900 hover:bg-black",       icon: Twitter,       url: kit.share_urls.twitter },
    { key: "telegram",  label: "Telegram",  color: "bg-sky-500 hover:bg-sky-600",         icon: Send,          url: kit.share_urls.telegram },
    { key: "linkedin",  label: "LinkedIn",  color: "bg-blue-700 hover:bg-blue-800",       icon: Linkedin,      url: kit.share_urls.linkedin },
    { key: "email",     label: "Email",     color: "bg-slate-600 hover:bg-slate-700",     icon: Mail,          url: kit.share_urls.email },
  ] : [];

  const openInsta = () => {
    if (!kit?.epaper_jpg_url) return toast.error("पहले E-paper JPG generate करें");
    // Instagram doesn't support URL share — user manually uploads the JPG
    toast.info("Step 1: JPG download हुआ · Step 2: Instagram खोलें और post/story बनायें", { duration: 6000 });
    const a = document.createElement("a");
    a.href = kit.epaper_jpg_url; a.download = `epaper-${slug}.jpg`; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.open("https://www.instagram.com/", "_blank"), 800);
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        className={`border-fuchsia-500 text-fuchsia-700 hover:bg-fuchsia-50 ${className}`}
        data-testid="social-share-btn">
        <Share2 className="w-4 h-4 mr-1.5"/> Share Everywhere
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-panel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-fuchsia-600"/> Share this news</DialogTitle>
            <DialogDescription className="line-clamp-2 text-sm">{title || kit?.title}</DialogDescription>
          </DialogHeader>

          {busy && <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-6 h-6 animate-spin inline mr-2"/> Share kit तैयार हो रहा है…</div>}

          {kit && !busy && (
            <div className="space-y-5">
              {/* E-paper preview */}
              <div className="rounded-xl border-2 border-slate-100 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-xs font-black text-slate-700 flex items-center gap-1.5"><ImgIcon className="w-4 h-4 text-amber-500"/>E-Paper JPG (auto-generated)</div>
                  <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" onClick={regenerate} disabled={regen} className="text-slate-600 h-7" data-testid="regen-epaper">
                      {regen ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <RefreshCw className="w-3 h-3 mr-1"/>} Refresh
                    </Button>
                    {kit.epaper_jpg_url && (
                      <a href={kit.epaper_jpg_url} download={`epaper-${slug}.jpg`} target="_blank" rel="noopener noreferrer" data-testid="download-epaper-jpg">
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white h-7"><Download className="w-3 h-3 mr-1"/>JPG</Button>
                      </a>
                    )}
                  </div>
                </div>
                {kit.epaper_jpg_url ? (
                  <img src={kit.epaper_jpg_url} alt="E-Paper preview" className="w-full max-h-96 object-contain bg-slate-100" data-testid="epaper-preview"/>
                ) : (
                  <div className="p-8 text-center text-slate-400 text-xs">E-paper अभी generate नहीं हुआ — "Refresh" पर click करें</div>
                )}
              </div>

              {/* Social platform grid */}
              <div>
                <div className="text-xs font-black text-slate-700 mb-2 uppercase tracking-widest">एक क्लिक में भेजें</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {platforms.map((p) => {
                    const Icon = p.icon;
                    return (
                      <a key={p.key} href={p.url} target="_blank" rel="noopener noreferrer"
                         className={`flex items-center gap-2 ${p.color} text-white font-bold text-sm py-2.5 px-3 rounded-lg transition`}
                         data-testid={`share-${p.key}`}>
                        <Icon className="w-4 h-4"/> {p.label}
                      </a>
                    );
                  })}
                  <button onClick={openInsta}
                    className="flex items-center gap-2 bg-gradient-to-r from-pink-500 via-fuchsia-500 to-orange-400 hover:brightness-110 text-white font-bold text-sm py-2.5 px-3 rounded-lg transition"
                    data-testid="share-instagram">
                    <Instagram className="w-4 h-4"/> Instagram
                  </button>
                </div>
                {kit.share_urls.whatsapp_channel && (
                  <a href={kit.share_urls.whatsapp_channel} target="_blank" rel="noopener noreferrer"
                     className="mt-2 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm py-2 px-3 rounded-lg transition"
                     data-testid="share-wa-channel">
                    <MessageCircle className="w-4 h-4"/> WhatsApp Channel में post करें
                  </a>
                )}
              </div>

              {/* Captions to copy */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-black text-slate-700 uppercase tracking-widest">पूरा Caption</div>
                    <Button variant="ghost" size="sm" onClick={() => copy(kit.caption_full)} className="h-6 text-slate-600" data-testid="copy-caption-full"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100 max-h-40 overflow-y-auto">{kit.caption_full}</pre>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-black text-slate-700 uppercase tracking-widest">Short Caption (Twitter/Insta)</div>
                    <Button variant="ghost" size="sm" onClick={() => copy(kit.caption_short)} className="h-6 text-slate-600" data-testid="copy-caption-short"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100">{kit.caption_short}</pre>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                <span>🔗</span>
                <code className="flex-1 truncate bg-slate-50 px-2 py-1 rounded">{kit.article_url}</code>
                <Button variant="ghost" size="sm" onClick={() => copy(kit.article_url)} className="h-6" data-testid="copy-article-url"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>बंद</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
